import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { useLocale } from '../i18n/LocaleContext';
import { isAppPath } from '../i18n/locales';
import { PAGES } from '../i18n/routes';
import { activeSeason, seasonPaidOffer } from '../growth/season';
import { useSubscriptionStore } from '../stores/subscriptionStore';

// The paid offer's banner is its own: closing October's free-video one did not refuse it
const dismissedKey = (season, paid) => `cs_season_dismissed_${season.key}${paid ? '_paid' : ''}`;

function wasDismissed(season, paid) {
  try {
    return window.localStorage.getItem(dismissedKey(season, paid)) === '1';
  } catch {
    return false;
  }
}

/**
 * The season's banner (growth/season.js): on the marketing pages and the AI Agent,
 * never on a work surface or on the season's own page, gone for good once closed.
 */
export default function SeasonalBanner({ now }) {
  const { locale, t } = useLocale();
  const { pathname } = useLocation();
  const { subscription } = useSubscriptionStore() || {};
  const subscriber = Boolean(subscription?.id) && ['active', 'trialing'].includes(subscription?.status);
  const season = activeSeason(now, locale);
  // While the season's offer is on it leads with the bonus videos for subscribing (October
  // 2026: 5), pointing at the pricing page; a subscriber already has them
  const paidVideos = !subscriber ? seasonPaidOffer(season, now || new Date()) : null;
  const paid = Boolean(paidVideos);
  const [dismissed, setDismissed] = useState(() => (season ? wasDismissed(season, paid) : true));
  if (!season || dismissed) return null;

  const to = paid ? PAGES.pricing?.[locale] : PAGES[season.page]?.[locale];
  const onAgent = pathname === '/ai-agent';
  if (!to || pathname === to || (isAppPath(pathname) && !onAgent)) return null;

  const close = () => {
    try { window.localStorage.setItem(dismissedKey(season, paid), '1'); } catch { /* shown again next visit */ }
    setDismissed(true);
  };
  const message = paid ? t('season.paidOffer', { videos: paidVideos }) : t('season.message');

  return (
    <div role="region" aria-label={t('season.label')} className="relative border-b border-orange-500/30 bg-gradient-to-r from-orange-600/20 via-purple-700/20 to-orange-600/20 text-sm text-ink">
      <div className="container mx-auto flex items-center justify-center gap-3 px-9 py-2 text-center sm:px-10 sm:py-2.5">
        <span aria-hidden="true">🎃</span>
        {paid ? (
          <>
            {/* One line on a phone (2026-10-05: five lines took a sixth of the screen), the whole sentence from 640 px */}
            <span className="sm:hidden" data-testid="season-short">{t('season.paidOfferShort', { videos: paidVideos })}</span>
            <span className="hidden sm:inline">{message}</span>
          </>
        ) : <span>{message}</span>}
        <Link to={to} className="inline-flex shrink-0 items-center gap-1 font-semibold text-orange-700 dark:text-orange-200 hover:text-ink hover:underline">
          {t(paid ? 'season.paidCta' : 'season.cta')} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
      <button type="button" onClick={close} aria-label={t('season.close')} className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-ink/60 hover:bg-ink/10 hover:text-ink">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
